/**
 * GameCamera — the top-down chase rig. Lead, drift, range, shake and fov punch all live
 * here, which makes this the file that decides what the game feels like at the wrist.
 */
import * as THREE from 'three';
import { CAMERA, MOVEMENT, LAB } from '../data/tuning.ts';
import { MOBILE, QUALITY } from '../data/quality.ts';

const DEG2RAD = THREE.MathUtils.DEG2RAD;

/* Stand-off, scaled for the device. A phone sits closer so the animal is big enough to read;
   see data/quality.ts. Derived once here rather than at every use, so the two distances cannot
   drift apart and the ventilation lerp between them stays exactly what it was. */
const NEAR = CAMERA.distanceNear * QUALITY.cameraZoom;
const FAR = CAMERA.distanceFar * QUALITY.cameraZoom;

/**
 * The aspect the framing was tuned at. Past this the rig pulls IN.
 *
 * three's fov is VERTICAL, so the visible world height is the same whatever the aspect and the
 * animal always fills the same fraction of the screen's HEIGHT. On a monitor that is the right
 * behaviour and nobody notices it. On a phone it is a trap: turned to landscape, the screen's
 * height is its short physical edge, so the same fraction of it is barely half the physical
 * size it was in portrait — which is why landscape looked no closer even after the rig moved
 * 20% in. The fix is to hold the visible WIDTH once the frame is wider than it was tuned for.
 */
const REF_ASPECT = 16 / 9;

/** how far the pinch may take it, as a multiple of the framed distance */
const ZOOM_MIN = 0.55;
const ZOOM_MAX = 1.55;
const TAU = Math.PI * 2;

/**
 * Frame-rate-independent exponential approach. Every follow term in this file goes
 * through it — a bare `lerp(a, b, 0.1)` is a different camera at 144 Hz than at 60 Hz,
 * and that difference is exactly the kind of thing that makes a game feel cheap on
 * someone else's machine.
 */
function approach(response: number, dt: number): number {
  return 1 - Math.exp(-response * dt);
}

/**
 * Shake frequency ladders, Hz. Each is the previous times the golden ratio, so the three
 * sines never re-phase inside a shake's lifetime — that is the whole trick that stops
 * summed sines reading as a periodic wobble, and it is why this is not random jitter.
 * X and Y use different ladders so the shake is not a diagonal line.
 */
const SHAKE_HZ_X: readonly number[] = [2.9, 4.69, 7.59];
const SHAKE_HZ_Y: readonly number[] = [3.41, 5.52, 8.93];
const SHAKE_PHASE: readonly number[] = [0, 1.13, 2.71];
const SHAKE_W: readonly number[] = [1.0, 0.55, 0.3];
const SHAKE_NORM = 1 / (SHAKE_W[0] + SHAKE_W[1] + SHAKE_W[2]);
/** A rotational component sells an impact that pure translation cannot; more than ~1° reads as a bug. */
const SHAKE_HZ_ROLL = 2.13;
const SHAKE_ROLL_PER_UNIT = 0.05;

/** Fraction of a punch spent going out. Fast out / slow back is what reads as an impact. */
const PUNCH_RISE = 0.28;

/**
 * Lab lens. CAMERA.fov (68°) keystones the outer sharks of a five-wide row badly enough
 * that they look like different species; ~26° is near-orthographic, so all ten read at
 * the same apparent size. Lab framing, not a gameplay tunable, so it is not in tuning.ts.
 */
const GALLERY_FOV_DEG = 26;
const GALLERY_FIT = 1.08;

export class GameCamera {
  readonly camera: THREE.PerspectiveCamera;

  private focusX = 0;
  private focusY = 0;
  private focusZ = 0;
  private leadX = 0;
  private leadZ = 0;
  private curDistance = NEAR;
  private zoom = 1;
  /** pull-in for frames wider than REF_ASPECT; see the note on that constant */
  private aspectFit = 1;
  private primed = false;

  private shakeAmp = 0;
  private shakeT = 0;
  private shakeDur = 0;

  private fovExtra = 0;
  private fovT = 0;
  private fovDur = 0;
  private appliedFov = CAMERA.fov;

  private gallery = false;
  private galleryX = 0;
  private galleryZ = 0;
  private galleryWidth = 1;

  private readonly ray = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly cursor = { x: 0, z: 0 };

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(CAMERA.fov, aspect, CAMERA.near, CAMERA.far);
    // groundPoint() unprojects through the *current* matrices, so the rig has to be
    // valid before the first follow() call ever asks it where the cursor is.
    this.compose();
  }

  get distance(): number {
    return this.curDistance;
  }

  /**
   * Player zoom, from a pinch. 1 is the framing the game was tuned at.
   *
   * Multiplies the rig's own distance rather than replacing it, so the pull-back with speed
   * still happens — it is the thing that buys reaction time, and a player who has zoomed in has
   * not asked to give that up.
   */
  setZoom(z: number): void {
    this.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
  }

  get zoomLevel(): number {
    return this.zoom;
  }

  resize(width: number, height: number): void {
    const aspect = height > 0 ? width / height : 1;
    if (this.camera.aspect !== aspect) {
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
    }
    /* Hold the visible WIDTH once the frame is wider than the one the game was framed at, and
       leave everything narrower alone. Visible width is 2*d*tan(fov/2)*aspect, so holding it
       means d scaling as 1/aspect. Only ever pulls in: a portrait phone already shows plenty of
       ground and pushing the camera out to match a 16:9 would put the shark in the distance.

       HARDER ON A PHONE, and the exponent is not a fudge — it is the difference between screen
       fraction and physical size. Holding the width alone still left the animal 1.8 cm across
       in landscape against 3.1 cm in portrait, because a landscape phone's height IS its short
       physical edge and a fixed fraction of it is simply smaller in the hand. On a monitor that
       does not apply, so an ultrawide desktop keeps the plain width rule and does not suddenly
       find itself sitting on top of the shark. */
    const fit = Math.min(1, REF_ASPECT / Math.max(0.2, aspect));
    this.aspectFit = MOBILE ? Math.pow(fit, 1.9) : fit;
    // The gallery solves distance from the horizontal fov, which depends on aspect.
    if (this.gallery) this.composeGallery();
  }

  follow(
    target: { x: number; y?: number; z: number },
    vel: { x: number; z: number },
    pointerNdc: { x: number; y: number },
    dt: number,
  ): void {
    if (this.gallery) {
      // Coming back from the lab the rig is ~130 u out; easing from there would be a
      // two-second fly-in every time the player hits Tab. Reacquire instead.
      this.gallery = false;
      this.primed = false;
    }

    const cruise = MOVEMENT.cruiseSpeed;
    const speed = Math.hypot(vel.x, vel.z);
    const vent = Math.min(speed / cruise, 1);

    // SPEC §13: 26 u at rest → 34 u at cruise. Pulling back with speed is what keeps the
    // player's stopping distance on screen.
    const wantDistance = (NEAR + (FAR - NEAR) * vent) * this.zoom * this.aspectFit;

    // Cursor lead reads the cursor through last frame's matrices. That one-frame lag is
    // deliberate: unprojecting through the camera we are about to move creates a feedback
    // loop between lead and position that oscillates at high response values.
    this.groundPoint(pointerNdc.x, pointerNdc.y, this.cursor);
    let lx = this.cursor.x - target.x;
    let lz = this.cursor.z - target.z;
    const leadLen = Math.hypot(lx, lz);
    if (leadLen > CAMERA.cursorLead) {
      const s = CAMERA.cursorLead / leadLen;
      lx *= s;
      lz *= s;
    }

    // velocityDrift is per unit of normalised speed, so it maxes out at CAMERA.velocityDrift
    // even mid-lunge (speed ~100 u/s). The lunge gets its camera event from punchFov.
    const driftScale = speed > 1e-4 ? (CAMERA.velocityDrift * vent) / speed : 0;
    const driftX = vel.x * driftScale;
    const driftZ = vel.z * driftScale;

    if (!this.primed) {
      this.primed = true;
      this.curDistance = wantDistance;
      this.leadX = lx;
      this.leadZ = lz;
      this.focusX = target.x + lx + driftX;
      this.focusZ = target.z + lz + driftZ;
      this.focusY = target.y ?? 0;
    } else {
      this.curDistance += (wantDistance - this.curDistance) * approach(CAMERA.distanceResponse, dt);

      const kLead = approach(CAMERA.cursorLeadResponse, dt);
      this.leadX += (lx - this.leadX) * kLead;
      this.leadZ += (lz - this.leadZ) * kLead;

      const kFollow = approach(CAMERA.followResponse, dt);
      this.focusX += (target.x + this.leadX + driftX - this.focusX) * kFollow;
      this.focusZ += (target.z + this.leadZ + driftZ - this.focusZ) * kFollow;
      // Depth tracks slower than XZ on purpose: a hard dive should drop the shark through
      // the frame before the camera catches up, which is where the sense of falling comes
      // from. Matching the XZ rate makes a dive feel like nothing happened.
      this.focusY += ((target.y ?? 0) - this.focusY) * approach(CAMERA.followResponse * 0.55, dt);
    }

    this.advanceShake(dt);
    this.advanceFov(dt);
    this.compose();
  }

  /** Static framing for the Phase 1 species lab: raised, long-lens, fits `width` across. */
  frameGallery(centerX: number, centerZ: number, width: number): void {
    this.gallery = true;
    this.galleryX = centerX;
    this.galleryZ = centerZ;
    this.galleryWidth = Math.max(width, 1e-3);
    this.shakeAmp = 0;
    this.shakeDur = 0;
    this.shakeT = 0;
    this.fovExtra = 0;
    this.fovDur = 0;
    this.fovT = 0;
    this.composeGallery();
  }

  addShake(amplitude: number, duration: number): void {
    if (amplitude <= 0 || duration <= 0) return;
    // A new shake must never make an in-flight one weaker or shorter, or a fast kill
    // chain cuts its own feedback off.
    const remaining = this.shakeEnvelope();
    this.shakeAmp = Math.max(amplitude, remaining);
    this.shakeDur = Math.max(duration, this.shakeDur - this.shakeT);
    this.shakeT = 0;
  }

  punchFov(extraDeg: number, duration: number): void {
    if (extraDeg <= 0 || duration <= 0) return;
    const current = this.fovDur > 0 ? this.fovExtra * punchEnvelope(this.fovT / this.fovDur) : 0;
    if (extraDeg <= current) return;

    this.fovExtra = extraDeg;
    this.fovDur = duration;
    // Re-enter the new envelope at the height we are already at; restarting from 0 would
    // snap the fov back to base for a frame. Closed-form inverse of smoothstep.
    const y = Math.min(current / extraDeg, 1);
    const s = 0.5 - Math.sin(Math.asin(1 - 2 * y) / 3);
    this.fovT = s * PUNCH_RISE * duration;
  }

  /** Ray from the current camera through NDC onto the y = 0 plane. Allocation-free. */
  groundPoint(ndcX: number, ndcY: number, out: { x: number; z: number }): void {
    const cam = this.camera;
    const v = this.ray.set(ndcX, ndcY, 0.5).unproject(cam);
    v.sub(cam.position);
    const len = v.length();
    if (len < 1e-6) {
      out.x = cam.position.x;
      out.z = cam.position.z;
      return;
    }
    v.multiplyScalar(1 / len);
    // Analytic ray/plane. A THREE.Raycaster here would allocate a Ray and a Plane on
    // every pointer sample, i.e. every frame.
    let t = v.y < -1e-5 ? -cam.position.y / v.y : CAMERA.far;
    if (t > CAMERA.far) t = CAMERA.far;
    out.x = cam.position.x + v.x * t;
    out.z = cam.position.z + v.z * t;
  }

  // ---------------------------------------------------------------------------------

  private shakeEnvelope(): number {
    if (this.shakeDur <= 0) return 0;
    // The linear tail is load-bearing: exp() alone still has amplitude left when the
    // timer expires, and cutting that off is a visible pop.
    return this.shakeAmp * Math.exp(-this.shakeT * CAMERA.shakeDecay) * (1 - this.shakeT / this.shakeDur);
  }

  private advanceShake(dt: number): void {
    if (this.shakeDur <= 0) return;
    this.shakeT += dt;
    if (this.shakeT >= this.shakeDur) {
      this.shakeDur = 0;
      this.shakeT = 0;
      this.shakeAmp = 0;
    }
  }

  private advanceFov(dt: number): void {
    let extra = 0;
    if (this.fovDur > 0) {
      this.fovT += dt;
      if (this.fovT >= this.fovDur) {
        this.fovDur = 0;
        this.fovT = 0;
        this.fovExtra = 0;
      } else {
        extra = this.fovExtra * punchEnvelope(this.fovT / this.fovDur);
      }
    }
    const want = CAMERA.fov + extra;
    if (Math.abs(want - this.appliedFov) > 1e-4) {
      this.appliedFov = want;
      this.camera.fov = want;
      this.camera.updateProjectionMatrix();
    }
  }

  private compose(): void {
    const pitch = CAMERA.pitchDeg * DEG2RAD;
    const sp = Math.sin(pitch);
    const cp = Math.cos(pitch);

    let ox = 0;
    let oy = 0;
    let oz = 0;
    let roll = 0;

    const env = this.shakeEnvelope();
    if (env > 0) {
      const t = this.shakeT;
      let sx = 0;
      let sy = 0;
      for (let i = 0; i < 3; i++) {
        sx += SHAKE_W[i] * Math.sin(TAU * SHAKE_HZ_X[i] * t + SHAKE_PHASE[i]);
        sy += SHAKE_W[i] * Math.sin(TAU * SHAKE_HZ_Y[i] * t + SHAKE_PHASE[2 - i]);
      }
      sx *= env * SHAKE_NORM;
      sy *= env * SHAKE_NORM;
      // Displace along the SCREEN axes, not world XZ, so a shake always reads as a shake
      // whatever the pitch. The rig has no yaw, so screen-right is world -X and screen-up
      // is (0, cos pitch, sin pitch).
      ox = -sx;
      oy = sy * cp;
      oz = sy * sp;
      roll = env * SHAKE_ROLL_PER_UNIT * Math.sin(TAU * SHAKE_HZ_ROLL * t + 0.6);
    }

    // The look-at point carries the same offset as the eye, so the shake is a pure
    // translation of the whole frustum. Shaking only the eye would swing the view around
    // a pinned target, which reads as the world teleporting rather than the camera being hit.
    const px = this.focusX + ox;
    const pz = this.focusZ + oz;
    const d = this.curDistance;

    this.camera.position.set(px, this.focusY + oy + d * sp, pz - d * cp);
    this.look.set(px, this.focusY + oy, pz);
    this.camera.lookAt(this.look);
    if (roll !== 0) this.camera.rotateZ(roll);
    this.camera.updateMatrixWorld(true);
  }

  private composeGallery(): void {
    const cam = this.camera;
    if (Math.abs(this.appliedFov - GALLERY_FOV_DEG) > 1e-4) {
      this.appliedFov = GALLERY_FOV_DEG;
      cam.fov = GALLERY_FOV_DEG;
      cam.updateProjectionMatrix();
    }

    const halfV = GALLERY_FOV_DEG * 0.5 * DEG2RAD;
    const halfH = Math.atan(Math.tan(halfV) * cam.aspect);
    this.curDistance = (this.galleryWidth * 0.5 * GALLERY_FIT) / Math.tan(halfH);

    // Elevation comes from the LAB rig so the lab's look stays art-directed from tuning;
    // only the range is solved for the requested width. Camera sits on +Z — in front of
    // the noses — because a species gallery has to show you the heads.
    const elev = Math.atan2(LAB.cameraHeight, LAB.cameraDistance);
    const d = this.curDistance;
    cam.position.set(this.galleryX, Math.sin(elev) * d, this.galleryZ + Math.cos(elev) * d);
    this.look.set(this.galleryX, 0, this.galleryZ);
    cam.lookAt(this.look);
    cam.updateMatrixWorld(true);
  }
}

/** Out on a smoothstep, back on a longer one. Peaks at exactly 1.0 at u = PUNCH_RISE. */
function punchEnvelope(u: number): number {
  const ss = THREE.MathUtils.smoothstep;
  return ss(u, 0, PUNCH_RISE) * (1 - ss(u, PUNCH_RISE, 1));
}
